/**
 * Header 组件
 * 具体应用的导航头部，使用纯UI的AppHeader组件并添加应用特定的路由和链接
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '@readsavor/ui';
import { RouteNames, getRoutePath } from '../../routes/index.js';
import { TransitionLink } from '../../routes/TransitionLink.js';
import { useAuth, useLogout, useAuthStore } from '@readsavor/auth';
import { CreditIndicator } from './CreditIndicator.js';

/**
 * ReadSavor应用的导航头部组件
 */
const Header: React.FC = () => {
  const { user, status, isError } = useAuth(); // 获取新的 status 状态
  const { isInitializationComplete } = useAuthStore();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();
  const navigate = useNavigate();

  const handleSignOut = () => {
    logout(undefined, {
      onSuccess: () => {
        navigate(getRoutePath(RouteNames.HOME));
      },
    });
  };

  return (
    <AppHeader
      slotLeft={
        <TransitionLink
          to={getRoutePath(RouteNames.HOME)}
          className="text-interactive-emerald-flash text-[28px] font-bold px-4 py-2"
        >
          ReadSavor
        </TransitionLink>
      }
      slotCenter={
        <nav className="flex items-center">
          <TransitionLink
            to={getRoutePath(RouteNames.LOCAL_VOCABULARY)}
            className="text-interactive-emerald-flash hover:text-[#FB923C] px-4 py-2 rounded-md text-base font-medium"
          >
            Vocabulary
          </TransitionLink>
          <TransitionLink
            to={getRoutePath(RouteNames.HISTORY)}
            className="text-interactive-emerald-flash hover:text-[#FB923C] px-4 py-2 rounded-md text-base font-medium"
          >
            History
          </TransitionLink>
          <TransitionLink
            to={getRoutePath(RouteNames.PRICING)}
            className="text-interactive-emerald-flash hover:text-[#FB923C] px-4 py-2 rounded-md text-base font-medium"
          >
            Pricing
          </TransitionLink>
        </nav>
      }
      slotRight={
        <div className="flex items-center">
          {status === 'pending' && (
            <span className="text-static-cream-yellow px-2 py-2 rounded-md text-base">
              Loading...
            </span>
          )}
          {isError && (
            <span className="text-interactive-coral-red px-2 py-2 rounded-md text-base">Error</span>
          )}
          {status === 'authenticated' &&
            user &&
            (!isInitializationComplete ? (
              <span className="text-static-cream-yellow px-2 py-2 rounded-md text-base animate-pulse">
                Initializing...
              </span>
            ) : (
              <>
                {/* 积分指示器 */}
                <CreditIndicator className="px-2" />
                <span className="text-interactive-emerald-flash px-2 py-2 rounded-md text-base font-medium">
                  {user.email}
                </span>

                <button
                  onClick={handleSignOut}
                  disabled={isLoggingOut}
                  className="text-interactive-emerald-flash hover:text-[#FB923C] px-2 py-2 rounded-md text-base font-medium"
                >
                  {isLoggingOut ? 'Signing out...' : 'Sign Out'}
                </button>
                <TransitionLink
                  to={getRoutePath(RouteNames.PROFILE)}
                  className="text-interactive-emerald-flash hover:text-[#FB923C] px-2 py-2 rounded-md text-base font-medium"
                >
                  Profile
                </TransitionLink>
              </>
            ))}
          {status === 'unauthenticated' && (
            <>
              <TransitionLink
                to={getRoutePath(RouteNames.LOGIN)}
                className="text-interactive-emerald-flash hover:text-[#FB923C] px-2 py-2 rounded-md text-base font-medium"
              >
                Sign In
              </TransitionLink>
              <TransitionLink
                to={getRoutePath(RouteNames.REGISTER)}
                className="text-interactive-emerald-flash hover:text-[#FB923C] px-2 py-2 rounded-md text-base font-medium"
              >
                Sign Up
              </TransitionLink>
            </>
          )}
        </div>
      }
    />
  );
};

export default Header;
